import { SecurityTask } from "../interfaces";

export function common(): string {
    return 'common';
}

export function toTaskDescription(task: SecurityTask, userNameResolver?: (uid: string) => string | undefined) {
    // TODO this can be dynamically updated to better indicate the action that was taken.
    // On the `task` it has the snapshot data that can be used to indicate what feature was added, what the policy was, etc.
    const resolveUserLabel = (uid: string | undefined): string => {
        if (!uid) return 'User';
        const resolved = userNameResolver?.(uid);
        if (resolved) return resolved;
        return uid.substring(0, 8);
    };
    switch (task.typeOfTask) {
        case 'updateSecurityGroup':
            return `Updated Security Group: ${task.snapshotOld?.name}`;
        case 'addSecurityGroup':
            return `Created Security Group: ${task.snapshotNew?.name}`;
        case 'removeSecurityGroup':
            return `Removed Security Group: ${task.snapshotOld?.name}`;
        case 'addFeature':
            return `Created Feature: ${task.snapshotNew?.name}`;
        case 'removeFeature':
            return `Removed Feature: ${task.snapshotOld?.name}`;
        case 'addFeaturePermissions':
            return `Created Feature Permissions`;
        case 'removeFeaturePermissions':
            return `Removed Feature Permissions`
        case 'addPolicy':
            return `Created Policy: ${task.snapshotNew.name}`;
        case 'removePolicy':
            return `Removed Policy: ${task.snapshotOld?.name}`;
        case 'updatePolicy':
            return `Updated Policy: ${task.snapshotOld?.name}`;
        case 'addHat':
            return `Created Hat: ${task.snapshotNew?.name}`;
        case 'removeHat':
            return `Removed Hat: ${task.snapshotOld?.name}`;
        case 'updateHat':
            return `Updated Hat: ${task.snapshotOld?.name}`;
        case 'addPart':
            return `Created Part: ${task.snapshotNew?.name}`;
        case 'removePart':
            return `Removed Part: ${task.snapshotOld?.name}`;
        case 'updatePart':
            return `Updated Part: ${task.snapshotOld?.name}`;
        case 'reorderChildren':
            return `Reordered Children: ${task.snapshotOld?.name || task.partKey?.substring(0, 8) || 'Part'}`;
        case 'bulkAddParts':
            return task.diffNew?.totalCount
                ? `Bulk Created ${task.diffNew.totalCount} Positions under: ${task.snapshotNew?.anchorPartKey?.substring(0, 8) || 'Part'}`
                : `Reordered Positions under: ${task.snapshotNew?.anchorPartKey?.substring(0, 8) || 'Part'}`;
        case 'bulkDeleteParts':
            return `Bulk Deleted ${task.diffNew?.totalCount || '?'} Positions`;
        case 'bulkSave': {
            const d: any = task.diffNew || {};
            const updates = Array.isArray(d.updates) ? d.updates.length : 0;
            const moves = Array.isArray(d.moves) ? d.moves.length : 0;
            const reorders = Array.isArray(d.reorders) ? d.reorders.length : 0;
            const deletes = Array.isArray(d.deletes) ? d.deletes.length : 0;
            const adds = d.add?.newParts?.length || 0;
            const parts = [
                updates ? `${updates} update${updates === 1 ? '' : 's'}` : '',
                moves ? `${moves} move${moves === 1 ? '' : 's'}` : '',
                adds ? `${adds} add${adds === 1 ? '' : 's'}` : '',
                reorders ? `${reorders} reorder${reorders === 1 ? '' : 's'}` : '',
                deletes ? `${deletes} delete${deletes === 1 ? '' : 's'}` : '',
            ].filter(Boolean);
            return parts.length ? `Bulk Save: ${parts.join(', ')}` : 'Bulk Save (no-op)';
        }
        case 'updateUser': {
            // For updateUser the snapshots hold the SG/hat document that
            // triggered the cascade — NOT the user. The actual user
            // reference is task.uid. The caller can pass `userNameResolver`
            // to convert it into a friendly display name (the org-board
            // and dashboards do this via `organization.orgUsers[uid].name`);
            // when no resolver is supplied we fall back to the first 8
            // chars of the uid so the row at least identifies the user.
            return `Updated User: ${resolveUserLabel(task.uid)}`;
        }
        case 'addUser':
            return `Added User: ${task.snapshotNew?.name || task.snapshotNew?.email || resolveUserLabel(task.uid)}`;
        case 'removeUser':
            return `Removed User: ${task.snapshotOld?.name || task.snapshotOld?.email || resolveUserLabel(task.uid)}`;
        default:
            return '--';
    }

}

// ─────────────────────────────────────────────────────────────────────────────
// Compact task presentation — shared between the edit-dialog "Pending Changes"
// panel and the org-board "Recent activity" popover so both surfaces describe a
// task with the same icon + name + action + status-icon layout.
// ─────────────────────────────────────────────────────────────────────────────

export interface TaskPresentation {
    /** Material icon name representing the action (e.g. 'edit', 'add_circle'). */
    icon: string;
    /** Short verb describing the action (e.g. 'update', 'moved', 'add hat'). */
    action: string;
    /** Best-effort human-readable subject (part name, hat name, etc.). */
    name: string;
    /** Material icon name representing the status (e.g. 'check_circle', 'sync'). */
    statusIcon: string;
    /** Lower-cased status label (e.g. 'completed', 'processing', 'failed'). */
    statusLabel: string;
    /** Optional dedup key; tasks sharing this key collapse into one row. */
    dedupKey: string;
}

export interface TaskPresentationContext {
    /** Org-part list used to resolve `partKey` → display name. */
    orgParts?: { partKey: string; name?: string }[];
    /** Hat map (orgHats) used to resolve `hatId` → display name. */
    orgHats?: Record<string, { name?: string; positionName?: string }>;
    /** When true, bulkSave / bulkAddParts render with the move icon instead of save/add. */
    reorderOnly?: boolean;
}

export function toTaskPresentation(
    task: SecurityTask,
    ctx: TaskPresentationContext = {}
): TaskPresentation {
    // ── Status icon + label ──────────────────────────────────────────────
    const isTerminal =
        task.status === 'completed' ||
        task.status === 'fulfilled' ||
        task.status === 'error' ||
        task.status === 'Error' ||
        task.status === 'failed';
    const isSuccess = task.status === 'completed' || task.status === 'fulfilled';

    let statusIcon = 'hourglass_empty';
    let statusLabel = 'queued';
    if (task.status === 'processing') {
        statusIcon = 'sync';
        statusLabel = 'processing';
    } else if (isSuccess) {
        statusIcon = 'check_circle';
        statusLabel = 'completed';
    } else if (isTerminal) {
        statusIcon = 'error';
        statusLabel = (task as any).error || 'failed';
    }

    // ── Action icon + label ──────────────────────────────────────────────
    let icon = 'settings';
    let action: string = task.typeOfTask || 'processing';
    if (task.typeOfTask === 'bulkSave' && ctx.reorderOnly) {
        icon = 'swap_vert';
        action = 'saving';
    } else if (task.typeOfTask === 'bulkSave') {
        icon = 'save';
        action = 'saving';
    } else if (task.typeOfTask === 'bulkAddParts' && ctx.reorderOnly) {
        icon = 'swap_vert';
        action = 'moved';
    } else if (task.typeOfTask === 'bulkAddParts') {
        icon = 'add_circle';
        action = 'bulk add';
    } else if (task.typeOfTask === 'updatePart') {
        icon = 'edit';
        action = 'update';
    } else if (task.typeOfTask === 'addPart') {
        icon = 'add_circle';
        action = 'add';
    } else if (task.typeOfTask === 'removePart') {
        icon = 'remove_circle';
        action = 'delete';
    } else if (task.typeOfTask === 'reorderChildren') {
        icon = 'swap_vert';
        action = 'moved';
    } else if (task.typeOfTask === 'bulkDeleteParts') {
        icon = 'remove_circle';
        action = 'bulk delete';
    } else if (task.typeOfTask === 'addHat') {
        icon = 'school';
        action = 'add hat';
    } else if (task.typeOfTask === 'updateHat') {
        icon = 'school';
        action = 'update hat';
    } else if (task.typeOfTask === 'removeHat') {
        icon = 'school';
        action = 'remove hat';
    } else if (task.typeOfTask === 'addUser') {
        icon = 'person_add';
        action = 'add user';
    } else if (task.typeOfTask === 'removeUser') {
        icon = 'person_remove';
        action = 'remove user';
    } else if (task.typeOfTask === 'updateUser') {
        icon = 'person';
        action = 'update user';
    } else if (task.typeOfTask === 'addSecurityGroup') {
        icon = 'lock';
        action = 'add SG';
    } else if (task.typeOfTask === 'removeSecurityGroup') {
        icon = 'lock_open';
        action = 'remove SG';
    } else if (task.typeOfTask === 'updateSecurityGroup') {
        icon = 'lock';
        action = 'update SG';
    } else if (task.typeOfTask === 'addPolicy' || task.typeOfTask === 'updatePolicy' || task.typeOfTask === 'removePolicy') {
        icon = 'policy';
        action = task.typeOfTask.replace('Policy', ' policy').toLowerCase();
    } else if (task.typeOfTask === 'addFeature' || task.typeOfTask === 'removeFeature') {
        icon = 'extension';
        action = task.typeOfTask === 'addFeature' ? 'add feature' : 'remove feature';
    }

    // ── Subject name resolution ──────────────────────────────────────────
    let name = 'Unknown';
    if (task.partKey) {
        const part = ctx.orgParts?.find(p => p.partKey === task.partKey);
        name = part?.name || task.snapshotNew?.name || (task as any).diffNew?.name || task.partKey;
    }
    if (name === 'Unknown' && (task as any).hatId) {
        const hatId = (task as any).hatId as string;
        const hat = ctx.orgHats?.[hatId];
        name = hat?.name || hat?.positionName || task.snapshotNew?.name || hatId;
    }
    if (name === 'Unknown') {
        name = task.snapshotNew?.name
            || task.snapshotOld?.name
            || (task as any).diffNew?.name
            || (task.partKey ? task.partKey.substring(0, 8) : '')
            || ((task as any).hatId ? (task as any).hatId.substring(0, 8) : '')
            || (task.key ? task.key.substring(0, 8) : 'Unknown');
    }

    // ── Dedup key ────────────────────────────────────────────────────────
    const isHatTask =
        task.typeOfTask === 'addHat' ||
        task.typeOfTask === 'updateHat' ||
        task.typeOfTask === 'removeHat';
    const hatPartKey = task.snapshotNew?.positionArray?.[0] || task.snapshotOld?.positionArray?.[0];
    const dedupId = isHatTask
        ? (task.partKey || hatPartKey || (task as any).hatId || task.key)
        : ((task as any).hatId || task.partKey || task.key);
    const dedupKey = `${task.typeOfTask}:${dedupId}`;

    return { icon, action, name, statusIcon, statusLabel, dedupKey };
}